import React, { useEffect, useRef } from 'react';
import { useLessonCreation } from '../../hooks/use-lesson-creation';
import { LoadingSpinner, ErrorState } from '../shared/components';
import LessonMetadata from './components/LessonMetadata';
import SlideBuilder from './components/SlideBuilder';
import CreationSidebar from './components/CreationSidebar';
import PreviewMode from './components/PreviewMode';
import type { LessonType } from '../../core/types/lesson.types';

import BgContentWrap from '../../../../components/layout/bg-content-wrap';

interface LessonCreatorProps {
  lessonType: LessonType;
  lessonId?: string;
}

export const LessonCreator: React.FC<LessonCreatorProps> = ({
  lessonType,
  lessonId,
}) => {
  const hookResult = useLessonCreation(lessonType, lessonId);

  const {
    // State
    lesson,
    selectedSlideId,
    errors,
    isDirty,

    // Loading states
    isLoading,
    isSaving,
    isSuccess: _isSuccess,
    isError,

    // Actions
    addSlide,
    removeSlide,
    updateSlide,
    duplicateSlide,
    reorderSlides,
    updateMetadata,
    selectSlide,
    save,
    startPreview,
    endPreview,

    // Config
    config,

    // Utility methods
    getCurrentSlideCount,
  } = hookResult;

  // Map hook properties to component interface
  const selectedSlide = lesson?.slides?.find(s => s.id === selectedSlideId);
   // Separate validation errors from React Query loading errors
  // - hasValidationErrors: Form/slide validation issues (show inline in sidebar)
  // - isError: React Query loading failure (show error page)
  const hasValidationErrors = Object.keys(errors).length > 0;
  const canSave = isDirty && !hasValidationErrors && !!lesson;
  const slideCount = getCurrentSlideCount();
  const saveLesson = () => save();
  const enterPreview = () => startPreview();
  const exitPreview = () => endPreview();
  const allowedSlideTypes = config?.features.allowedSlideTypes || [];
  const isPreviewing = hookResult.isPreviewing;

  // Warn about unsaved changes before leaving
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isDirty) {
        e.preventDefault();
        e.returnValue = 'Ai modificări nesalvate. Ești sigur că vrei să părăsești pagina?';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty]);

  // Ref for scrollable content area
  const contentRef = useRef<HTMLDivElement>(null);

  // Scroll to bottom when slide is added
  const handleAddSlide = (type: string) => {
    addSlide(type);
    // Wait for DOM update, then scroll
    setTimeout(() => {
      if (contentRef.current) {
        contentRef.current.scrollTo({
          top: contentRef.current.scrollHeight,
          behavior: 'smooth'
        });
      }
    }, 100);
  };

  // Loading state
  if (isLoading) {
    return (
      <BgContentWrap customStyles="flex items-center justify-center">
        <LoadingSpinner size="lg" message="Se încarcă lecția..." />
      </BgContentWrap>
    );
  }

  // Error state - ONLY for React Query loading errors, NOT validation errors
  // Validation errors are shown inline in sidebar while form stays editable
  if (isError) {
    return (
      <BgContentWrap customStyles="flex items-center justify-center">
        <ErrorState
          message="Nu s-a putut încărca lecția"
          details="Verifică conexiunea la internet și încearcă din nou"
        />
      </BgContentWrap>
    );
  }

  // Preview mode
  if (isPreviewing && lesson) {
    return <PreviewMode lesson={lesson} onExit={exitPreview} />;
  }

  // Main editing view
  return (
    <div className="flex-1 flex flex-row bg-[url('/quiz-bg.jpg')] bg-cover bg-center overflow-hidden">
      {/* Main Content Area */}
      <div ref={contentRef} className="flex-1 overflow-y-auto pr-4 ecotic-table-scrollbar">
        <div className="max-w-6xl mx-auto space-y-6 p-4">
          {/* Page Header */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-foreground">
                {lessonId ? 'Editează lecție' : 'Lecție nouă'}
              </h1>
              <p className="text-sm text-muted-foreground mt-1">
                {config.displayName}
              </p>
            </div>

            {lessonId && (
              <div className="text-sm text-muted-foreground">
                ID: {lessonId}
              </div>
            )}
          </div>

          {/* Lesson Metadata */}
          <LessonMetadata
            lesson={lesson}
            config={config}
            errors={errors}
            onChange={updateMetadata}
          />

          {/* Slide Builder */}
          <div className="slide-editor-container">
            <SlideBuilder
              slides={lesson?.slides || []}
              selectedSlideId={selectedSlide?.id || null}
              allowedTypes={allowedSlideTypes}
              errors={errors}
              onSelectSlide={selectSlide}
              onUpdateSlide={updateSlide}
              onRemoveSlide={removeSlide}
              onDuplicateSlide={duplicateSlide}
              onReorderSlides={reorderSlides}
            />
          </div>
        </div>
      </div>

      {/* Sidebar */}
      <CreationSidebar
        lesson={lesson}
        config={config}
        allowedSlideTypes={allowedSlideTypes}
        canSave={canSave}
        isSaving={isSaving}
        isDirty={isDirty}
        slideCount={slideCount}
        errors={errors}
        onAddSlide={handleAddSlide}
        onSave={saveLesson}
        onPreview={enterPreview}
      />
    </div>
  );
};

export default LessonCreator;
